import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import {
  Context,
  Editor,
  EventInfo,
  Marker,
  MarkerCollection,
} from 'ckeditor5';
import { documentApi } from '../../api/document';
import {
  COMMENT_SET_DELETED_BY_USER_ERROR,
  logCustomError,
} from '../../messages/LogErrorMessages';
import { retryOnError } from '../../util/APIUtils';
import { formTargetsFromMarker } from './eventHandlerUtil';

const RETRIES = 3;

/**
 * @description Handles events for removing/resolving comment threads or individual comment (multiroot)
 */
export function handleRemoveCommentThread(
  channelId: string,
  threadId: string,
  companyId: number,
  documentId: number,
  commentId?: string,
) {
  documentApi
    .setDeletedByUserCommentThreads(
      companyId,
      documentId,
      threadId,
      channelId,
      commentId,
    )
    .catch((error) => {
      retryOnError(
        () =>
          documentApi.setDeletedByUserCommentThreads(
            companyId,
            documentId,
            threadId,
            channelId,
            commentId,
          ),
        200,
        error,
        RETRIES,
      );
    });
}

export function setupDeletedByUserHandlers(
  editor: Editor | Context,
  currentUser: CurrentUser,
  companyId?: number,
  documentId?: number,
): void {
  const commentsRepository = editor.plugins.get('CommentsRepository') as any;
  const isAuthoredByCurrentUser = (comment: CommentsRepository.Comment) =>
    comment.author?.id === currentUser.email;
  const setDeletedByCurrentUser = (comment: CommentsRepository.Comment) => {
    try {
      comment.setAttribute('deleted_by_user', currentUser.fullName);
    } catch (error) {
      logCustomError(COMMENT_SET_DELETED_BY_USER_ERROR, { error });
    }
  };

  const onThreadRemoval = (
    _evt: EventInfo,
    data: CommentsRepository.RemoveCommentThreadData,
  ): void => {
    if (data.isFromAdapter) {
      return;
    }
    const thread = commentsRepository.getCommentThread(data.threadId);
    let requiresBackendUpdate = false;
    thread?.comments.forEach((comment: CommentsRepository.Comment) => {
      if (isAuthoredByCurrentUser(comment)) {
        setDeletedByCurrentUser(comment);
      } else {
        requiresBackendUpdate = true;
      }
    });
    if (
      requiresBackendUpdate &&
      companyId !== undefined &&
      documentId !== undefined
    ) {
      handleRemoveCommentThread(
        data.channelId,
        data.threadId,
        companyId,
        documentId,
      );
    }
  };

  commentsRepository?.on('resolveCommentThread', onThreadRemoval);
  commentsRepository?.on('removeCommentThread', onThreadRemoval, {
    priority: 'high',
  });
  commentsRepository?.on(
    'removeComment',
    (_evt: EventInfo, data: CommentsRepository.RemoveCommentData) => {
      if (data.isFromAdapter) {
        return;
      }
      const thread = commentsRepository.getCommentThread(data.threadId);
      const comment = thread?.comments.get(data.commentId);
      if (!comment) {
        return;
      }
      if (isAuthoredByCurrentUser(comment)) {
        setDeletedByCurrentUser(comment);
      } else if (companyId !== undefined && documentId !== undefined) {
        // Defer to the backend for comments we did not author (see above).
        handleRemoveCommentThread(
          data.channelId,
          data.threadId,
          companyId,
          documentId,
          data.commentId,
        );
      }
    },
    { priority: 'high' },
  );
}

export function handleComment(
  evt: EventInfo,
  commentData: CommentsRepository.CommentData,
  markerCollection: MarkerCollection,
  currentUser: CurrentUser,
) {
  if (commentData.isFromAdapter) {
    return; // this is a comment loaded from the backend
  }
  setAuthorNameAttribute(commentData, currentUser);
  const commentIsFirstInThread =
    (evt.source as any).getCommentThread(commentData.threadId)?.comments
      .length === 0;
  const commentMarker = getCommentMarker(
    markerCollection,
    commentData.threadId,
  );
  const suggestionMarker = getSuggestionMarker(
    markerCollection,
    commentData.threadId,
  );
  if (commentMarker && commentIsFirstInThread) {
    setContentCommentedOnAttribute(commentData, commentMarker);
  }
  if (suggestionMarker) {
    setSuggestionIdAttribute(commentData);
  }
}

export function handleAddFileDocumentComment(
  evt: EventInfo,
  commentData: CommentsRepository.CommentData,
  currentUser: CurrentUser,
  filename: string,
): void {
  const isFirstCommentInThread =
    (evt.source as any).getCommentThread(commentData.threadId)?.comments
      .length === 0;
  setAuthorNameAttribute(commentData, currentUser);
  if (isFirstCommentInThread) {
    setFileCommentedOnAttribute(commentData, filename);
  }
}

function setAuthorNameAttribute(
  commentData: CommentsRepository.CommentData,
  currentUser: CurrentUser,
) {
  if (commentData.attributes === undefined) {
    commentData.attributes = {
      authorFullName: currentUser.fullName,
    };
  } else {
    commentData.attributes.authorFullName = currentUser.fullName;
  }
}

function setContentCommentedOnAttribute(
  commentData: CommentsRepository.CommentData,
  marker: Marker,
) {
  let commentTargets: CommentsRepository.Target[] = [];
  commentTargets = commentTargets.concat(formTargetsFromMarker(marker));
  commentData.attributes.contentCommentedOn = commentTargets;
}

function setFileCommentedOnAttribute(
  commentData: CommentsRepository.CommentData,
  filename: string,
) {
  // There are no markers available on file document comments,
  // so just set the target to the filename
  commentData.attributes.contentCommentedOn = [
    {
      name: 'text',
      data: filename,
    },
  ];
}

function setSuggestionIdAttribute(commentData: CommentsRepository.CommentData) {
  commentData.attributes.suggestionId = commentData.threadId;
}

function getSuggestionMarker(
  markerCollection: MarkerCollection,
  threadId: string,
): Marker | undefined {
  let suggestionMarker;
  for (const suggestionMarkers of [
    markerCollection.getMarkersGroup('suggestion'),
  ]) {
    for (const marker of suggestionMarkers) {
      if (marker.name.indexOf(threadId) > -1) {
        suggestionMarker = marker;
      }
    }
  }
  return suggestionMarker;
}

function getCommentMarker(
  markerCollection: MarkerCollection,
  threadId: string,
): Marker | undefined {
  let commentMarker;
  for (const commentMarkers of [markerCollection.getMarkersGroup('comment')]) {
    for (const marker of commentMarkers) {
      if (marker.name.indexOf(threadId) > -1) {
        commentMarker = marker;
      }
    }
  }
  return commentMarker;
}
